import api from '../../api/config';
import { LOOKUP } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import {
  findUnitedStatesId,
  getCachedCountries as getSessionCachedCountries,
  setCachedCountries as setSessionCachedCountries,
  setCachedUsCountryId,
  getCachedSourcesPayload,
  setCachedSourcesPayload,
  getCachedBrandsPayload,
  setCachedBrandsPayload,
} from '../../utils/lookupSessionCache';

const cache = {
  sources: null,
  brands: null,
  countries: null,
  closers: null,
};

export function clearLeadFormOptionsCache() {
  cache.sources = null;
  cache.brands = null;
  cache.countries = null;
  cache.closers = null;
}

export function mapStatusOptions(raw) {
  return (Array.isArray(raw) ? raw : []).map((s) => ({ id: s.id, name: s.name }));
}

export function mapSourceOptions(raw) {
  return (Array.isArray(raw) ? raw : []).map((s) => ({ id: s?.id, name: s?.name }));
}

export function mapBrandOptions(raw) {
  return (Array.isArray(raw) ? raw : []).map((b) => ({ id: b?.id, name: b?.name }));
}

export function mapCountryOptions(raw) {
  return (Array.isArray(raw) ? raw : []).map((c) => ({ id: c?.id, name: c?.name }));
}

export function mapCloserOptions(users) {
  if (!Array.isArray(users) || users.length === 0) return [];
  return users.map((u) => ({
    id: u.uuid || u.id || u._id,
    name: u.name || u.fullName || u.username || '—',
    role: u.roles?.[0]?.name || u.role || 'Closer',
  }));
}

export function getCachedSources() {
  if (cache.sources) return cache.sources;
  const session = getCachedSourcesPayload();
  if (session != null) {
    cache.sources = mapSourceOptions(extractList(session));
    return cache.sources;
  }
  return null;
}

export function getCachedBrands() {
  if (cache.brands) return cache.brands;
  const session = getCachedBrandsPayload();
  if (session != null) {
    cache.brands = mapBrandOptions(extractList(session));
    return cache.brands;
  }
  return null;
}

export function getCachedCountries() {
  if (cache.countries) return cache.countries;
  const sessionCountries = getSessionCachedCountries();
  if (sessionCountries?.length) {
    cache.countries = mapCountryOptions(sessionCountries);
    return cache.countries;
  }
  return null;
}

export function getCachedClosers() {
  return cache.closers;
}

export function setCachedSources(options) {
  cache.sources = options;
  if (Array.isArray(options) && options.length) {
    setCachedSourcesPayload(options);
  }
}

export function setCachedBrands(options) {
  cache.brands = options;
  if (Array.isArray(options) && options.length) {
    setCachedBrandsPayload(options);
  }
}

export function setCachedCountries(options) {
  cache.countries = options;
  if (Array.isArray(options) && options.length) {
    setSessionCachedCountries(options);
    const usId = findUnitedStatesId(options);
    if (usId != null) setCachedUsCountryId(usId);
  }
}

export function setCachedClosers(options) {
  cache.closers = options;
}

/** Warm dropdown caches on lead list mount so Initiate Deal opens faster. */
export async function preloadLeadFormOptions(dispatch, thunks) {
  const { getLeadSources, getLeadBrands, getUsersByRole } = thunks;
  const tasks = [];

  if (!cache.sources) {
    const sessionSources = getCachedSourcesPayload();
    if (sessionSources != null) {
      cache.sources = mapSourceOptions(extractList(sessionSources));
    } else {
      tasks.push(
        dispatch(getLeadSources())
          .unwrap()
          .then((res) => {
            cache.sources = mapSourceOptions(extractList(res));
          })
          .catch(() => {})
      );
    }
  }

  if (!cache.brands) {
    const sessionBrands = getCachedBrandsPayload();
    if (sessionBrands != null) {
      cache.brands = mapBrandOptions(extractList(sessionBrands));
    } else {
      tasks.push(
        dispatch(getLeadBrands())
          .unwrap()
          .then((res) => {
            cache.brands = mapBrandOptions(extractList(res));
          })
          .catch(() => {})
      );
    }
  }

  if (!cache.countries) {
    const sessionCountries = getSessionCachedCountries();
    if (sessionCountries?.length) {
      cache.countries = mapCountryOptions(sessionCountries);
    } else {
      tasks.push(
        api
          .get(LOOKUP.COUNTRIES)
          .then((res) => {
            const raw =
              res?.data?.data?.list ?? res?.data?.list ?? extractList(res?.data);
            cache.countries = mapCountryOptions(raw);
            setSessionCachedCountries(raw);
            const usId = findUnitedStatesId(raw);
            if (usId != null) setCachedUsCountryId(usId);
          })
          .catch(() => {})
      );
    }
  }

  if (!cache.closers) {
    tasks.push(
      dispatch(getUsersByRole('Closer'))
        .unwrap()
        .then((payload) => {
          const users = Array.isArray(payload)
            ? payload
            : payload?.data ?? payload?.users ?? payload?.results ?? [];
          cache.closers = mapCloserOptions(users);
        })
        .catch(() => {})
    );
  }

  await Promise.all(tasks);
}
